const R = require('ramda');

const { getToday } = require('./utils');
const metricUtils = require('./utils/metricUtils');
const utils = require('./utils/utils');
const { getTrackerConversions } = require('./utils/utils');

const constants = require('./constants');
const {
  PRIORITY_METRICS,
  AD_METRICS,
  CAMPAIGN_METRICS,
  EXTRA_CAMPAIGN_METRICS,
  GOOGLE_SUBCHANNELS,
  GRAPH_METRICS_BY_LEVEL,
  GRAPH_METRICS,
  AUDIENCE_METRICS,
  EXTRA_AUDIENCE_METRICS,
} = require('./constants');

const INDICATOR = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
};

const moment = require('moment');

const CONVERSION_METRICS = [
  'roas',
  'cpa',
  'conversions',
  'conversionRate',
  // 'engagements',
];
const BASIC_METRICS = ['impressions', 'clicks', 'ctr', 'cpc', 'engagements', 'spend', 'amountSpentPercentage'];
const ACTIVITY_METRICS = ['bounceRate', 'newVisits', 'visits', 'visitDuration', 'pageViews'];

const isBasicMetric = (() => {
  const _set = new Set(BASIC_METRICS);

  return v => _set.has(v);
})();
const isActivityMetric = (() => {
  const _set = new Set(ACTIVITY_METRICS);

  return v => _set.has(v);
})();
const isConversionMetric = (() => {
  const _set = new Set(CONVERSION_METRICS);

  return v => _set.has(v);
})();

const conversionOrIncluded = (metricList = []) => {
  const _set = new Set(metricList);

  return name => _set.has(name) || name.indexOf('conversion_') === 0;
};

function getSubGroupWeight(name, metricSet = []) {
  const metricLength = metricSet.length;
  if (!metricLength || !name) {
    return 0;
  }
  const index = metricSet.findIndex(R.equals(name));
  return (1 / (metricLength + 1)) * (index + 1);
}

// function getMetricLevel(name, availableMetrics) {
const getMetricLevel = R.memoizeWith(R.identity, name => {
  if (isBasicMetric(name)) {
    return 2 + getSubGroupWeight(name, BASIC_METRICS);
  } else if (isActivityMetric(name)) {
    return 4 + getSubGroupWeight(name, ACTIVITY_METRICS);
  } else if (isConversionMetric(name)) {
    return 1 + getSubGroupWeight(name, CONVERSION_METRICS);
  }
  return 1;
});

function objToArr(obj, type = 'number', level = 3) {
  return Object.entries(obj).map(([key, value]) => ({
    name: key,
    data: {
      type,
      value,
    },
    level,
  }));
}

const EXTRA_METRIC = {
  visitDuration: 'visitDurationWithoutBounce',
  impressions: 'reach',
  visits: 'newVisits',
};

function formatMetric(name, metricObj, conversionObj, kpiObj) {
  if (name.indexOf('conversion_') === 0) {
    return formatConversionMetric(name, metricObj, conversionObj);
  }

  const value = metricUtils.getMetricValue({
    name,
    metricObj,
  });
  // if (value === undefined) return undefined;

  const formattedMetric = {
    name,
    data: {
      type: metricUtils.getCellType(name),
      value,
    },
  };
  if (kpiObj && kpiObj[name] != null) {
    formattedMetric.data.kpiValue = kpiObj[name];
  }
  if (EXTRA_METRIC[name]) {
    const extra = formatMetric(EXTRA_METRIC[name], metricObj, null, kpiObj);
    if (extra) formattedMetric.extra = extra;
  }

  return formattedMetric;
}

function formatConversionMetric(name, metricObj, conversionObj) {
  let [, tracker, ...eventName] = name.split('_');
  let isCPA = false;
  if (name.endsWith('_cpa')) {
    isCPA = true;
    eventName.pop();
  }
  eventName = eventName.join('_');
  let value = {
    tracker: tracker,
    name: eventName,
    value: undefined,
  };
  if (conversionObj) {
    if (conversionObj[tracker]) {
      // const conversion = conversionObj[tracker].find(
      //     R.propEq('name', eventName)
      // );
      const conversion = conversionObj[tracker].find(event => event.name == eventName);
      if (conversion) {
        if (isCPA) {
          if (!conversion.conversions) {
            value.value = null;
          }
          value.value = conversion.spendOnActions / conversion.conversions;
        } else {
          value.value = conversion.conversions;
        }
      }
    }
  }
  return {
    name,
    data: {
      type: isCPA ? 'conversionCPA' : 'conversion',
      value,
    },
  };
}

function metricToArr({ metricObj, kpiObj, conversionObj, selectedMetrics, computeLevels = false }) {
  if (!metricObj) {
    return [];
  }

  selectedMetrics = selectedMetrics || PRIORITY_METRICS;

  const metrics = selectedMetrics.reduce((acc, name) => {
    const data = formatMetric(name, metricObj, conversionObj, kpiObj);
    if (data) {
      acc.push(data);
    }

    return acc;
  }, []);

  if (computeLevels) {
    const availableMetrics = metrics.map(R.prop('name'));

    metrics.forEach(metric => (metric.level = Math.floor(getMetricLevel(metric.name, availableMetrics))));
  }
  return metrics;
  // const filteredMetrics = sortedMetrics.filter(data => data.level < 4);
  // return filteredMetrics;
}

function formatConversionsByTracker(conversionsByTracker) {
  if (!conversionsByTracker) {
    return null;
  }

  const selectedMetrics = [
    'cpa',
    'conversions',
    'spend',
    'clicks',
    'spendOnActions',
    'clicksOnActions',
    'conversionRate',
  ];

  return Object.entries(conversionsByTracker).reduce((acc, [tracker, actions]) => {
    acc[tracker] = actions.map(action => {
      action.conversions = action.value;
      return {
        name: action.name,
        metrics: metricToArr({
          metricObj: action,
          selectedMetrics,
        }),
      };
    });
    return acc;
  }, {});
}

function getSummaryMetrics({ architecture }) {
  const hasConversions = architecture.availableConversionNames.length > 0;
  let selectedMetrics = R.intersection(
    [
      'ctr',
      'cpc',
      'clicks',
      'impressions',
      'spend',
      'amountSpentPercentage',
      'visitDuration',
      'bounceRate',
      'pageViews',
      'visits',
    ],
    architecture.availableMetrics,
  );
  if (hasConversions) {
    selectedMetrics = ['cpa', 'conversions', 'conversionRate'].concat(selectedMetrics);
  }

  const metrics = metricToArr({
    metricObj: architecture.metric,
    selectedMetrics,
    computeLevels: true,
  });

  return metrics;
}

function getCampaigns({ architecture }) {
  return architecture.getCampaignsPerDimensions({
    dimensionIds: ['adrielCampaign'],
  });
}

function getSubChannels({ architecture }) {
  const subChannels = architecture.getDataBySubChannel();
  return subChannels;

  // if (!availableMetrics) availableMetrics = architecture.availableMetrics;
  // if (!availableConversionNames)
  //     availableConversionNames = architecture.availableConversionNames;
  // const subChannels = architecture.getDataBySubChannel().map((subChannel) =>
  //     formatSubChannel({
  //         subChannel,
  //         availableMetrics,
  //         availableConversionNames,
  //     })
  // );
  // return subChannels;
}

function getOverview({ architecture, filter }) {
  const OVERVIEW_AUDIENCE_DETAILS = ['newAndReturning', 'gender', 'location', 'age'];

  const base = { filter, architecture, isOverview: true };

  const availableMetrics = architecture.availableMetrics;
  const availableConversionNames = architecture.availableConversionNames;
  const campaigns = getCampaigns({
    architecture,
    availableMetrics,
    availableConversionNames,
  });
  const subChannels = getSubChannels({
    architecture,
    availableMetrics,
    availableConversionNames,
  });
  const leadFormCampaigns = getLeadFormCampaigns({ architecture });
  const metrics = getSummaryMetrics({ architecture });

  const keywords = getKeywords(base);
  const creatives = getCreatives(base);
  let audience = getAudience({
    ...base,
    details: OVERVIEW_AUDIENCE_DETAILS,
  });

  // sort by clicks
  audience = audience.map(audienceItem => {
    audienceItem.values.sort((a, b) => b.cell.clicks - a.cell.clicks);
    return audienceItem;
  });

  const conversionsByTracker = architecture.getConversionsByTracker();
  const conversions = formatConversionsByTracker(conversionsByTracker);
  const lastRefresh = architecture.getLastRefresh();

  const _take = R.take(10);

  const overviewObj = {
    lastRefresh,
    metrics,
    leadFormCampaigns,
    campaigns,
    conversions,
    subChannels,
    keywords: keywords ? _take(keywords) : keywords,
    audience: audience
      ? audience.map(
          audienceObj => ({
            ...audienceObj,
            values: _take(audienceObj.values),
          }),
          audience,
        )
      : audience,
    creatives,
    projects: [],
  };
  return overviewObj;
}

function getLeadFormCampaigns({ architecture }) {
  return architecture.adrielCampaigns
    .filter(adrielCampaign => adrielCampaign.isLeadForm)
    .map(adrielCampaign => ({
      id: adrielCampaign.id,
      title: adrielCampaign.title,
    }));
}

function getGraphMetricList({ architecture, availableMetrics, availableConversionNames }) {
  if (!availableMetrics) availableMetrics = architecture.availableMetrics;
  if (!availableConversionNames) availableConversionNames = architecture.availableConversionNames;
  const graphAvailableMetrics = [...availableMetrics];
  if (availableConversionNames.length > 0) graphAvailableMetrics.push('conversions', 'cpa', 'conversionRate');

  // TODO
  // check flat() is needed
  const metricList = GRAPH_METRICS_BY_LEVEL.map((levelMetrics, index) => {
    return R.intersection(levelMetrics, graphAvailableMetrics).map(metric => ({
      level: index + 1,
      type: metricUtils.getCellType(metric),
      name: metric,
    }));
  }, []).flat();

  return metricList;
}

function getPerformance({ architecture }) {
  const availableMetrics = architecture.availableMetrics;
  const availableConversionNames = architecture.availableConversionNames;
  const campaigns = getGridFormat({ architecture });
  const subChannels = getSubChannels({
    architecture,
    availableMetrics,
    availableConversionNames,
  });
  const metricList = getGraphMetricList({
    architecture,
    availableMetrics,
    availableConversionNames,
  });

  const performanceObj = {
    lastRefresh: architecture.getLastRefresh(),
    campaigns,
    subChannels,
    metricList,
  };

  return performanceObj;
}

/**
 * @param  {Object} obj - An object.
 * @param  {AdAccountArchitecture} obj.architecture
 */
function getAudience({ architecture, details }) {
  if (!details) details = ['location', 'gender', 'age', 'deviceTarget', 'newAndReturning'];

  const availableMetrics = architecture.availableMetrics;
  const availableConversionNames = architecture.availableConversionNames;
  let selectedMetrics = [...AUDIENCE_METRICS];
  if (availableConversionNames) {
    addConversionInSelectedMetric(selectedMetrics, availableConversionNames);
  }

  selectedMetrics = selectedMetrics.filter(conversionOrIncluded([...EXTRA_AUDIENCE_METRICS, ...availableMetrics]));

  const audience = details
    .map(detail => {
      const values = architecture.getDetailValues(detail);
      if (!values || values.length === 0) return null;
      if (detail === 'newAndReturning' && values.length === 1) return null;
      return {
        name: detail,
        values,
      };
    })
    .filter(Boolean);
  return audience;
}

function formatCampaign({ campaign, availableMetrics, availableConversionNames }) {
  let selectedMetrics = [...CAMPAIGN_METRICS];
  const pickExtra = R.pick(EXTRA_CAMPAIGN_METRICS);

  if (availableConversionNames) {
    addConversionInSelectedMetric(selectedMetrics, availableConversionNames);
  }
  if (campaign.channelCampaigns) {
    campaign.channelCampaigns
      .reduce((acc, channelCampaign) => {
        const conversionObj = channelCampaign.conversionsByTracker;
        const metricObj = {
          ...channelCampaign.metric,
          ...pickExtra(channelCampaign),
        };

        channelCampaign.adAccountId = campaign.adAccountId;

        channelCampaign.metrics = metricToArr({
          metricObj,
          kpiObj: channelCampaign.kpi,
          conversionObj,
          selectedMetrics,
        });
        // TODO: slow, O(n2)
        if (channelCampaign.adSets) {
          return [...acc, ...channelCampaign.adSets];
        } else return acc;
      }, [])
      .reduce((acc, adSet) => {
        const conversionObj = adSet.conversionsByTracker;
        const metricObj = {
          ...adSet.metric,
          ...pickExtra(adSet),
        };

        adSet.metrics = metricToArr({
          metricObj,
          kpiObj: adSet.kpi,
          conversionObj,
          selectedMetrics,
        });
        // TODO: slow, O(n2)
        return [...acc, ...adSet.ads];
      }, [])
      .forEach(ad => {
        const conversionObj = ad.conversionsByTracker;
        const metricObj = {
          ...ad.metric,
          ...pickExtra(ad),
          placements: ad.adSet.placements,
        };
        ad.metrics = metricToArr({
          metricObj,
          kpiObj: ad.kpi,
          conversionObj,
          selectedMetrics,
        });
      });
  }

  if (availableMetrics && availableMetrics.length > 0) {
    selectedMetrics = selectedMetrics.filter(conversionOrIncluded([...EXTRA_CAMPAIGN_METRICS, ...availableMetrics]));
  }
  const campaignMetric = {
    campaignId: campaign.id,
    ...pickExtra(campaign),
    conversionEvents: campaign.chosenConversions,
    ...campaign.metric,
    conversionsByTracker: campaign.conversions,
  };
  const conversionObj = campaign.conversionsByTracker;
  const metrics = metricToArr({
    metricObj: campaignMetric,
    kpiObj: campaign.kpi,
    conversionObj,
    selectedMetrics,
    sortByLevel: false,
  });
  const allMetrics = metricToArr({
    metricObj: campaignMetric,
    conversionObj,
    selectedMetrics: Object.keys(campaignMetric),
    sortByLevel: false,
  });
  const conversions = formatConversionsByTracker(conversionObj);

  let { subChannelMetrics } = campaign;
  const subChannelList = subChannelMetrics ? new Set(subChannelMetrics.map(R.prop('subChannel'))) : new Set();
  const hasGoogleSubChannel = GOOGLE_SUBCHANNELS.some(googleSubChannel => subChannelList.has(googleSubChannel));
  if (hasGoogleSubChannel && !subChannelList.has('google')) {
    let dailyBudget = 0;
    if (campaign.adrielCampaign) {
      dailyBudget = campaign.adrielCampaign.getSubChannelBudget('google');
    }
    subChannelMetrics.push({
      subChannel: 'google',
      metric: {
        dailyBudget,
      },
      actions: [],
    });
  }

  if (subChannelMetrics) {
    // if google budget remove budget for google subChannel
    const google = subChannelMetrics.find(R.propEq('subChannel', 'google'));
    if (google && R.path(['metric', 'dailyBudget'], google) > 0) {
      subChannelMetrics = subChannelMetrics.map(subChannelMetric => {
        if (GOOGLE_SUBCHANNELS.includes(subChannelMetric.subChannel)) {
          return {
            ...subChannelMetric,
            metric: R.omit(['dailyBudget'], subChannelMetric.metric),
          };
        }
        return subChannelMetric;
      });
    }
  }
  return {
    ...R.pick(
      [
        'id',
        'channelId',
        'createdAt',
        'title',
        'image',
        'channels',
        'objectives',
        'isCustom',
        'schedule',
        'status',
        'good',
        'isAdrielLeadForm',
        'dailyBudget',
        'channelCampaigns',
        'chosenConversions',
        'adAccountId',
        'projectId',
      ],
      campaign,
    ),
    conversions,
    subChannelMetrics: subChannelMetrics
      ? subChannelMetrics.map(subChannel =>
          formatSubChannel({
            subChannel,
            availableMetrics,
            availableConversionNames,
          }),
        )
      : [],
    metrics,
    allMetrics,
  };
}

function addConversionInSelectedMetric(selectedMetrics, conversionName) {
  const conversionsIndex = selectedMetrics.indexOf('INSERT_CONVERSIONS');
  if (conversionsIndex === -1) return;
  selectedMetrics.splice(conversionsIndex, 1, ...conversionName.map(name => [name, `${name}_cpa`]).flat());
}

function formatSubChannel({ subChannel, availableMetrics, availableConversionNames }) {
  const _set = new Set(['objectives', 'subChannels', 'status', 'appPlatforms', 'campaignId', 'dailyBudget']);

  let selectedMetrics = CAMPAIGN_METRICS.filter(name => !_set.has(name));

  if (availableConversionNames) {
    addConversionInSelectedMetric(selectedMetrics, availableConversionNames);
  }
  if (availableMetrics && availableMetrics.length > 0) {
    selectedMetrics = selectedMetrics.filter(conversionOrIncluded([...['conversionEvents'], ...availableMetrics]));
  }
  const conversionObj = subChannel.conversionsByTracker;
  const metric = {
    ...subChannel.metric,
  };
  const metrics = metricToArr({
    metricObj: metric,
    conversionObj,
    selectedMetrics,
  });
  const allMetrics = metricToArr({
    metricObj: metric,
    conversionObj,
    selectedMetrics: Object.keys(metric),
  });

  return {
    ...R.pick(['subChannel'], subChannel),
    metrics,
    conversions: formatConversionsByTracker(subChannel.conversionsByTracker),
    allMetrics,
  };
}

function fillDateGapWithZero(data, dateGroup = 'day') {
  // if (dateGroup !== 'day') return;
  const nbDates = data.length;
  if (nbDates > 1) {
    const { type } = data[0];
    for (let i = 0; i < data.length - 1 && i < 100; i++) {
      const currentDate = moment(data[i].date);
      const nextElementDate = moment(data[i + 1].date);
      // const currentDate = parseISO(data[i].date);
      // const nextElementDate = parseISO(data[i + 1].date);
      let nextDate;
      nextDate = currentDate.add(1, dateGroup);
      if (dateGroup === 'month') {
        nextDate = nextDate.startOf('month');
      } else if (dateGroup === 'week') {
        nextDate = nextDate.startOf('isoWeek');
      }
      if (!nextDate.isSame(nextElementDate, 'day')) {
        // if (!isSameDay(nextDate, nextElementDate)) {
        // insert missing day
        data.splice(i + 1, 0, {
          date: nextDate.format('YYYY-MM-DD'),
          type,
          value: 0,
        });
      }
    }
  }

  // Removed because it shows wrong data if there is a date filter
  // Add a zero before and after the graph to make nicer looking-curves.
  // if(nbDates >= 1){
  //     data.unshift({
  //         date: moment(data[0].date).add(-1, dateGroup).format('YYYY-MM-DD'),
  //         type: data[0].type,
  //         value: 0,
  //     });
  //     data.push({
  //         date: moment(data[data.length-1].date).add(1, dateGroup).format('YYYY-MM-DD'),
  //         type: data[0].type,
  //         value: 0,
  //     })
  // }
}

function formatGraphData({
  metricSelected,
  campaign,
  subChannelDataPerDate,
  tracker,
  conversionName,
  dateGroup = 'day',
}) {
  const obj = campaign ? campaign : subChannelDataPerDate;
  let reportsPerDate;
  if (!obj.conversionsAndMetricPerDate) return null;
  if (tracker) {
    reportsPerDate = obj.conversionsAndMetricPerDate
      .map(el => {
        const { from, conversionsByTracker } = el;
        if (!conversionsByTracker || !conversionsByTracker[tracker]) return null;
        const trackerConversions = conversionsByTracker[tracker];
        if (!trackerConversions) return null;
        const metric = trackerConversions.find(R.propEq('name', conversionName));
        if (!metric) return null;
        return {
          from,
          metric,
        };
      })
      .filter(Boolean);
  } else {
    reportsPerDate = obj.conversionsAndMetricPerDate;
  }

  const getCellType = R.memoizeWith(R.identity, metricUtils.getCellType);

  let transform = R.compose(
    R.map(R.props(['from', 'metric'])),
    R.map(([date, metricObj]) => {
      if (!metricObj) return null;

      return {
        date,
        type: getCellType(metricSelected),
        value: metricUtils.getMetricValue({
          name: metricSelected,
          metricObj,
        }),
      };
    }),
    R.filter(line => line && line.value != null),
  );

  const data = R.transduce(
    transform,
    (acc, v) => {
      acc.push(v);
      return acc;
    },
    [],
    reportsPerDate,
  ).sort((lineA, lineB) => new Date(lineA.date) - new Date(lineB.date));

  // fill the date gap with 0 value
  fillDateGapWithZero(data, dateGroup);
  if (data.length === 0) {
    return null;
  }
  return {
    ...R.pick(['id', 'channel', 'isCustom'], obj),
    title: obj.title || obj.name || obj.subChannel,
    data,
  };
}

/**
 * @param  {Object} obj - An object.
 * @param  {AdAccountArchitecture} obj.architecture
 * @param  {GoogleAnalyticsData} obj.googleAnalyticsData
 * @param  {number} obj.accountId
 * @param  {Account} obj.account
 * @param  {Object} obj.filter
 */
function getCreatives({ architecture, isOverview = false, filter }) {
  const ads = architecture.getAds();
  return ads;
}

/**
 * @param  {Object} obj - An object.
 * @param  {AdAccountArchitecture} obj.architecture
 */
function getKeywords({ architecture }) {
  return architecture.getDetailValues('searchKeywords');
}

/**
 *
 * @param {Object} obj - An object.
 * @param {AdAccountArchitecture} obj.architecture
 * @param {Array<String>} elementIds
 * @return {Object} computedMetric
 */
function getGraphSummaryMetrics({ architecture, elementIds, subChannels }) {
  let metricAndConversionList = [];
  if (subChannels && subChannels.length > 0) {
    metricAndConversionList = architecture
      .getDataBySubChannel({ cellFormat: false })
      .filter(({ subChannel }) => subChannels.includes(subChannel));
  } else if (elementIds && elementIds.length > 0) {
    metricAndConversionList = elementIds
      .map(elementId => {
        const { level } = utils.getInfoFromId(elementId);
        const element = architecture.elementsByIdByLevel[level || 'adrielCampaign'][elementId];
        return element;
      })
      .filter(Boolean)
      .map(element => ({
        metric: element.metric,
        conversionsByTracker: element.getConversionsByTracker(),
      }));
  }
  const { metric, conversionsByTracker } = metricUtils.mergeMetricAndConversion(metricAndConversionList);
  const computedMetric = metricUtils.getComputedMetric({
    metric,
    conversionsByTracker,
    metricList: architecture.availableMetrics,
  });
  return computedMetric;
}

function getOverviewGraph({ architecture, dateGroup }) {
  const dataList = architecture.getConversionAndMetric({
    dateGroup,
  });

  const overviewData = {
    date: [],
  };

  for (let i = 0; i < dataList.length; i++) {
    const { to, from, metric, conversionsByTracker } = dataList[i];
    const date = from || to;
    const computedMetric = metricUtils.getComputedMetric({
      metric,
      metricList: getAvailableCells({ architecture }),
      conversionsByTracker,
    });
    overviewData.date.push(date);

    for (const property in computedMetric) {
      if (!overviewData[property]) {
        overviewData[property] = new Array(dataList.length).fill(0);
      }
      const value = computedMetric[property] ? computedMetric[property] : 0;
      overviewData[property][i] = value;
    }
  }

  return overviewData;
}

function getOverviewMetric({ architecture }) {
  const { metric, conversionsByTracker } = architecture.getConversionAndMetric();

  const computedMetric = metricUtils.getComputedMetric({
    metric,
    metricList: getAvailableCells({ architecture }),
    conversionsByTracker,
  });
  return computedMetric;
}

function getGraph({ architecture, metric, filter = {}, graphType = 'campaign', tracker, conversionName }) {
  const { dateGroup } = filter;

  // for 1 campaign we show the graph per channel
  if (graphType === 'subChannel') {
    const dataBySubChannelPerDate = architecture.getDataBySubChannel({
      dateGroup,
    });
    const subChannelsGraph = dataBySubChannelPerDate
      .map(subChannelDataPerDate =>
        formatGraphData({
          metricSelected: metric,
          subChannelDataPerDate,
          tracker,
          conversionName,
          dateGroup,
        }),
      )
      .filter(Boolean);
    return subChannelsGraph;
  } else {
    const dimensionIds = ['adrielCampaign'];

    // using tranduce to save memory usage
    const withCampaign = R.assoc('campaign', R.__, {
      metricSelected: metric,
      tracker,
      conversionName,
      dateGroup,
    });

    let transform = R.compose(
      R.filter(R.prop('conversionsAndMetricPerDate')),
      R.map(withCampaign),
      R.map(formatGraphData),
      R.filter(R.identity),
    );

    return R.transduce(
      transform,
      (acc, v) => {
        acc.push(v);
        return acc;
      },
      [],
      architecture.getCampaignsPerDimensions({
        dimensionIds,
        includeChildren: false,
        dateGroup,
      }),
    );
  }
}

function getCampaignsPerDimensions({ architecture, dimensions = ['campaigns', 'adSets', 'ads'] }) {
  // const chanelMock = require('./mock_chanel.json');
  // return chanelMock;
  return architecture.getCampaignsPerDimensions({ dimensionIds: dimensions });
}

function getGridFormat({ architecture, dimensions = [] }) {
  const dimensionIds = [...dimensions];
  // Adriel Campaign dimension should be at the bottom always
  const indexOfCampaignDimension = dimensionIds.indexOf('campaign');
  if (indexOfCampaignDimension === -1) {
    dimensionIds.push('adrielCampaign');
  } else {
    dimensionIds.splice(indexOfCampaignDimension, 0, 'adrielCampaign');
  }
  return architecture.getCampaignsPerDimensions({ dimensionIds });
}

/**
 * Get all the available cells from the architecture
 * @param {AdAccountArchitecture} architecture
 * @param {'campaigns'|'creatives'|'audience'|'platforms'|'keywords'} tableType
 * @return {Array<string>} cellNames
 */
function getAvailableCells({ architecture, tableType }) {
  const metrics = architecture.availableMetrics;
  const conversionMetrics = architecture.availableConversionNames.reduce((acc, conversionName) => {
    acc.push(`conversions:${conversionName}`, `cpa:${conversionName}`);
    if (architecture.actionAttributionWindows && architecture.actionAttributionWindows.length) {
      for (const attrWid of architecture.actionAttributionWindows) {
        acc.push(`conversions:${conversionName}:${attrWid}`);
      }
    }
    return acc;
  }, []);

  let metricColumns = [...constants.COMMON_METRIC_COLUMNS];
  let basicColumns = [...constants.COMMON_BASIC_COLUMNS];
  let addConversionMetrics = true;

  switch (tableType) {
    case 'creatives':
      basicColumns = basicColumns.concat(constants.EXTRA_BASIC_AD_COLUMNS);
      break;
    case 'campaigns':
      basicColumns = basicColumns.concat(constants.EXTRA_BASIC_CAMPAIGN_COLUMNS);
      metricColumns = metricColumns.concat(constants.EXTRA_CAMPAIGN_METRIC_COLUMNS);
      break;
    case 'platforms':
      // for the subChannels we don't have other cell than the metrics
      basicColumns = [];
      break;
    case 'audience':
    case 'keywords':
      basicColumns = basicColumns.concat(constants.EXTRA_BASIC_AUDIENCE_COLUMNS);
      break;
  }

  metricColumns = metrics.filter(cellName => metricColumns.includes(cellName));

  if (addConversionMetrics) {
    metricColumns = metricColumns.concat(conversionMetrics);
  }

  return basicColumns.concat(metricColumns);
}

function getSubChannelList({ architecture }) {
  return architecture.getSubChannelList({
    includeFacebookSubChannels: false,
  });
}

/**
 * @param {Object} obj
 * @param {Array<Object>} obj.adAccountAssets
 * @param {AdAccountArchitecture} obj.architecture
 * @returns {Array<{ channel: String, indicatorObj: Object }>} channels
 */
function getChannelListWithLoadingStatus({ adAccountAssets, architecture }) {
  const adAccountAssetsWithIndicators = getAdAccountListWithLoadingStatus({
    adAccountAssets,
    architecture,
  });
  const channelsObjWithIndicator = adAccountAssetsWithIndicators.reduce((acc, adAccount) => {
    const { channel, loadingIndicatorObj } = adAccount;
    let previousIndicator = acc[channel];
    if (!previousIndicator) {
      acc[channel] = loadingIndicatorObj;
    } else {
      Object.keys(loadingIndicatorObj).forEach(indicator => {
        const previousStatus = previousIndicator[indicator];
        const currentStatus = loadingIndicatorObj[indicator];
        if ([previousStatus, currentStatus].includes(INDICATOR.ERROR)) {
          acc[channel][indicator] = INDICATOR.ERROR;
        } else if ([previousStatus, currentStatus].includes(INDICATOR.PENDING)) {
          acc[channel][indicator] = INDICATOR.PENDING;
        } else if (previousStatus === INDICATOR.COMPLETED && currentStatus === INDICATOR.COMPLETED) {
          acc[channel][indicator] = INDICATOR.COMPLETED;
        }
      });
    }
    return acc;
  }, {});
  const channelsWithIndicator = Object.entries(channelsObjWithIndicator).map(([channel, loadingIndicatorObj]) => {
    let status = INDICATOR.PENDING;
    const loadingIndicators = Object.values(loadingIndicatorObj);
    if (loadingIndicators.some(indicator => indicator === INDICATOR.ERROR)) {
      status = INDICATOR.ERROR;
    } else if (loadingIndicators.some(indicator => indicator === INDICATOR.PENDING)) {
      status = INDICATOR.PENDING;
    } else {
      status = INDICATOR.COMPLETED;
    }

    return {
      channel,
      status,
      loadingIndicatorObj,
    };
  });
  return channelsWithIndicator;
}

/**
 * @param {Object} obj
 * @param {Array<Object>} obj.adAccountAssets
 * @param {AdAccountArchitecture} obj.architecture
 * @returns {Array<Object>} adAccounts
 */
function getAdAccountListWithLoadingStatus({ adAccountAssets, architecture }) {
  const adAccountAssetsWithIndicators = adAccountAssets.map(adAccountAsset => {
    const { channel, adAccountId } = adAccountAsset;
    let architectureLoadingStatus = INDICATOR.PENDING;
    let reportsLoadingStatus = INDICATOR.PENDING;
    let graphLoadingStatus = INDICATOR.PENDING;
    const adAccountElement = architecture.adAccountsById[`${channel}:adAccount:${adAccountId}`];
    architectureLoadingStatus = adAccountElement
      ? INDICATOR.COMPLETED
      : architecture.isArchitectureLoaded
      ? INDICATOR.ERROR
      : INDICATOR.PENDING;

    if (architectureLoadingStatus === 'COMPLETED') {
      if (adAccountElement.isReportsLoaded || adAccountElement.campaigns.length === 0) {
        reportsLoadingStatus = INDICATOR.COMPLETED;
      } else if (architecture.isReportsLoaded) {
        reportsLoadingStatus = INDICATOR.ERROR;
      }

      if (adAccountElement.isGraphLoaded || adAccountElement.campaigns.length === 0) {
        graphLoadingStatus = INDICATOR.COMPLETED;
      } else if (architecture.isGraphLoaded) {
        graphLoadingStatus = INDICATOR.ERROR;
      }
    }

    const loadingIndicatorObj = {
      architectureLoadingStatus,
      reportsLoadingStatus,
      graphLoadingStatus,
    };

    return { ...adAccountAsset, loadingIndicatorObj };
  });
  return adAccountAssetsWithIndicators;
}

const dashboardHelpers = {
  getOverview,
  getSummaryMetrics,
  getPerformance,
  getGraph,
  getAudience,
  getCreatives,
  getSubChannels,
  getKeywords,
  formatCampaign,
  formatGraphData,
  getCampaigns,
  getCampaignsPerDimensions,
  getGridFormat,
  getAvailableCells,
  getGraphSummaryMetrics,
  getProjects: () => [],
  getSubChannelList,
  getOverviewGraph,
  getOverviewMetric,
  getChannelListWithLoadingStatus,
  getAdAccountListWithLoadingStatus,
};

module.exports = dashboardHelpers;

// FE only and for debug purpose
if (typeof window !== 'undefined') {
  window.dashboardHelpers = dashboardHelpers;
}
